import { useEffect, useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Box,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tabs,
  Tab,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import ClearAllIcon from '@material-ui/icons/ClearAll';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  serviceRestartPermission,
  serviceCacheClearPermission,
} from '../../permissions';

const CACHE_LANGUAGES = ['laravel', 'symfony', 'django'];

interface EnvConfig {
  key: string;
  displayName: string;
  namespace: string;
  color: string;
}

interface ActionState {
  restarting: boolean;
  cachingClear: boolean;
  restartError?: string;
  cacheError?: string;
  restartSuccess?: string;
  cacheSuccess?: string;
}

const useStyles = makeStyles(theme => ({
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 40,
    '& .MuiTab-root': {
      minHeight: 40,
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      letterSpacing: '0.06em',
    },
  },
  table: {
    '& th': {
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: theme.palette.text.secondary,
      borderBottom: `2px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 2),
    },
    '& td': {
      padding: theme.spacing(1.5, 2),
    },
  },
  successText: {
    color: '#4caf50',
    fontSize: '0.75rem',
    marginTop: 4,
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.75rem',
    marginTop: 4,
  },
  empty: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
}));

export const ServiceActionsContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendBaseUrl = configApi.getString('backend.baseUrl');

  const annotations = entity.metadata.annotations ?? {};
  const labelSelector = annotations['backstage.io/kubernetes-label-selector'];
  const language = annotations['fondative.io/language'];
  const hasCacheCommands = !!language && CACHE_LANGUAGES.includes(language.toLowerCase());

  const { allowed: canRestart } = usePermission({ permission: serviceRestartPermission });
  const { allowed: canCacheClear } = usePermission({ permission: serviceCacheClearPermission });

  const [activeTab, setActiveTab] = useState(0);
  const [envs, setEnvs] = useState<EnvConfig[]>([]);
  const [states, setStates] = useState<Record<string, ActionState>>({});
  const [restartDialog, setRestartDialog] = useState<{ open: boolean; envKey?: string }>({ open: false });

  useEffect(() => {
    try {
      const arr = configApi.getConfigArray('fondative.environments');
      setEnvs(
        arr.map(c => ({
          key: c.getString('key'),
          displayName: c.getString('displayName'),
          namespace: c.getOptionalString('namespace') ?? 'default',
          color: c.getOptionalString('color') ?? '#1976d2',
        })),
      );
    } catch {
      setEnvs([]);
    }
  }, [configApi]);

  const updateState = (key: string, update: Partial<ActionState>) =>
    setStates(prev => ({
      ...prev,
      [key]: { ...prev[key], ...update },
    }));

  const handleRestart = useCallback(
    async (envKey: string) => {
      const env = envs.find(e => e.key === envKey);
      if (!env || !labelSelector) return;
      setRestartDialog({ open: false });
      updateState(envKey, { restarting: true, restartError: undefined, restartSuccess: undefined });
      try {
        const res = await fetchApi.fetch(`${backendBaseUrl}/api/service-actions/restart`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ namespace: env.namespace, labelSelector }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        const result = await res.json();
        updateState(envKey, { restarting: false, restartSuccess: `${result.deployment} redémarré` });
      } catch (err: any) {
        updateState(envKey, { restarting: false, restartError: err.message });
      }
    },
    [envs, labelSelector, backendBaseUrl, fetchApi],
  );

  const handleCacheClear = useCallback(
    async (envKey: string) => {
      const env = envs.find(e => e.key === envKey);
      if (!env || !labelSelector || !language) return;
      updateState(envKey, { cachingClear: true, cacheError: undefined, cacheSuccess: undefined });
      try {
        const res = await fetchApi.fetch(`${backendBaseUrl}/api/service-actions/cache-clear`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ namespace: env.namespace, labelSelector, language }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        updateState(envKey, { cachingClear: false, cacheSuccess: 'Cache vidé' });
      } catch (err: any) {
        updateState(envKey, { cachingClear: false, cacheError: err.message });
      }
    },
    [envs, labelSelector, language, backendBaseUrl, fetchApi],
  );

  if (!labelSelector) {
    return (
      <Box p={3}>
        <Typography color="textSecondary">
          Ajoutez l'annotation <code>backstage.io/kubernetes-label-selector</code> pour activer les actions.
        </Typography>
      </Box>
    );
  }

  if (!envs.length) return null;

  const currentEnv = envs[activeTab];
  const state = states[currentEnv.key] ?? { restarting: false, cachingClear: false };
  const busy = state.restarting || state.cachingClear;

  return (
    <>
      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          className={classes.tabs}
          indicatorColor="primary"
          textColor="primary"
        >
          {envs.map(env => (
            <Tab key={env.key} label={env.displayName} style={{ color: env.color }} />
          ))}
        </Tabs>
        <CardContent style={{ padding: 0 }}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Action</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Exécuter</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Restart Service */}
              {canRestart && (
                <TableRow hover>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      Restart Service
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      Rolling restart des pods (zéro downtime)
                    </Typography>
                    {state.restartSuccess && (
                      <Typography className={classes.successText}>{state.restartSuccess}</Typography>
                    )}
                    {state.restartError && (
                      <Typography className={classes.errorText}>{state.restartError}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={busy}
                      startIcon={
                        state.restarting
                          ? <CircularProgress size={14} color="inherit" />
                          : <RefreshIcon fontSize="small" />
                      }
                      onClick={() => setRestartDialog({ open: true, envKey: currentEnv.key })}
                      style={{ textTransform: 'none' }}
                    >
                      Restart
                    </Button>
                  </TableCell>
                </TableRow>
              )}

              {/* Cache Clear */}
              {canCacheClear && hasCacheCommands && (
                <TableRow hover>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      Cache Clear
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      Vider le cache applicatif ({language})
                    </Typography>
                    {state.cacheSuccess && (
                      <Typography className={classes.successText}>{state.cacheSuccess}</Typography>
                    )}
                    {state.cacheError && (
                      <Typography className={classes.errorText}>{state.cacheError}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={busy}
                      startIcon={
                        state.cachingClear
                          ? <CircularProgress size={14} color="inherit" />
                          : <ClearAllIcon fontSize="small" />
                      }
                      onClick={() => handleCacheClear(currentEnv.key)}
                      style={{ textTransform: 'none' }}
                    >
                      Vider
                    </Button>
                  </TableCell>
                </TableRow>
              )}

              {!canRestart && !canCacheClear && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Box className={classes.empty}>
                      <Typography color="textSecondary">Droits insuffisants</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={restartDialog.open} onClose={() => setRestartDialog({ open: false })}>
        <DialogTitle>Confirmer le redémarrage</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Rolling restart sur <strong>{currentEnv.displayName}</strong> — les pods seront
            remplacés progressivement (zéro downtime).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestartDialog({ open: false })}>Annuler</Button>
          <Button
            color="primary"
            onClick={() => restartDialog.envKey && handleRestart(restartDialog.envKey)}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
